import { useContext, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { AuthContext } from "../../authContext";
import languages from "../../localization";
import useStore from "../../store/store";
import institutionalRoutes from "../../config/institutionalRoutes.json";
import {
  API_BASE_URL,
  DEFAULT_SOCIAL_IMAGE,
  PUBLIC_SITE_URL,
  SITE_NAME,
  SUPPORTED_SEO_LANGUAGES,
  absoluteAssetUrl,
  absoluteSiteUrl,
  localizedRouteUrl,
  normalizePathname,
} from "../../config/siteConfig";

const DEFAULT_DESCRIPTION =
  "Official Saba University website for academic programs, online registration, Kankor results, research, news, library resources, and student services.";

const pageCopy = {
  en: {
    homeTitle: "Saba University | Academic Programs, Admissions and Research",
    homeDescription: DEFAULT_DESCRIPTION,
    contactTitle: "Contact Saba University",
    contactDescription: "Official contact information, address, phone, email, and location details for Saba University.",
    newsTitle: "Saba University News and Announcements",
    newsDescription: "Read official Saba University news, public announcements, activities, and academic updates.",
    jobsTitle: "Job Opportunities at Saba University",
    jobsDescription: "Browse public job opportunities and official employment announcements from Saba University.",
    libraryTitle: "Saba University Library",
    libraryDescription: "Explore library services, academic resources, books, online library access, and study support at Saba University.",
    registrationTitle: "Online Registration | Saba University",
    registrationDescription: "Start the official Saba University online registration process for Grade 12 and Grade 14 applicants.",
    kankorTitle: "Kankor Results | Saba University",
    kankorDescription: "Search public approved Saba University Kankor result cards by official student or result identifier.",
    researchTitle: "Research at Saba University",
    researchDescription: "Explore Saba University research publications, research support, guides, trainings, and academic magazines.",
    studentsTitle: "Student Services | Saba University",
    studentsDescription: "Find Saba University student services, eligibility criteria, policies, scholarships, counseling, and academic guidance.",
    academicsTitle: "Academic Programs | Saba University",
    academicsDescription: "Explore Saba University faculties, departments, curriculum, labs, fees, academic calendar, and quality information.",
    aboutTitle: "About Saba University",
    aboutDescription: "Learn about Saba University achievements, leadership, organization, mission, and institutional values.",
    foundationTitle: "Saba Foundation",
    foundationDescription: "Learn about Saba Foundation initiatives, public service, and institutional development activity.",
    pdcTitle: "Professional Development Center | Saba University",
    pdcDescription: "Explore Saba University's Professional Development Center, trainings, workshops, teachers, students, and administrative staff resources.",
    notFoundTitle: "Page Not Found | Saba University",
    notFoundDescription: "The requested Saba University page could not be found. Continue to public university information and services.",
  },
  ps: {
    homeTitle: "سبا پوهنتون | علمي پروګرامونه، نوم لیکنه او څېړنه",
    homeDescription: "د سبا پوهنتون رسمي وېبپاڼه د علمي پروګرامونو، آنلاین نوم لیکنې، کانکور پایلو، څېړنې، خبرونو، کتابتون او محصل خدماتو لپاره.",
    contactTitle: "له سبا پوهنتون سره اړیکه",
    contactDescription: "د سبا پوهنتون رسمي پته، تلیفون، برېښنالیک او د اړیکې معلومات.",
    newsTitle: "د سبا پوهنتون خبرونه او اعلانونه",
    newsDescription: "د سبا پوهنتون رسمي خبرونه، اعلانونه، فعالیتونه او علمي تازه معلومات.",
    jobsTitle: "د سبا پوهنتون دندې",
    jobsDescription: "د سبا پوهنتون رسمي دندې او د استخدام اعلانونه وګورئ.",
    libraryTitle: "د سبا پوهنتون کتابتون",
    libraryDescription: "د سبا پوهنتون کتابتون، علمي سرچینې، آنلاین کتابتون او د مطالعې ملاتړ.",
    registrationTitle: "آنلاین نوم لیکنه | سبا پوهنتون",
    registrationDescription: "د دوولسم او څوارلسم ټولګي فارغانو لپاره د سبا پوهنتون رسمي آنلاین نوم لیکنه پیل کړئ.",
    kankorTitle: "د کانکور پایلې | سبا پوهنتون",
    kankorDescription: "د سبا پوهنتون تایید شوې عامه کانکور پایله د رسمي پېژندونکي له لارې ولټوئ.",
    researchTitle: "د سبا پوهنتون څېړنه",
    researchDescription: "د سبا پوهنتون څېړنیزې خپرونې، ملاتړ، لارښودونه، روزنې او علمي مجلې وګورئ.",
    studentsTitle: "د محصلانو خدمات | سبا پوهنتون",
    studentsDescription: "د سبا پوهنتون د محصلانو خدمات، شرایط، پالیسۍ، بورسونه، مشورې او علمي لارښود.",
    academicsTitle: "علمي پروګرامونه | سبا پوهنتون",
    academicsDescription: "د سبا پوهنتون پوهنځي، څانګې، نصاب، لابراتوارونه، فیسونه، علمي جنتري او کیفیت معلومات.",
    aboutTitle: "د سبا پوهنتون په اړه",
    aboutDescription: "د سبا پوهنتون تاریخ، لاسته راوړنې، مشرتابه، جوړښت، ماموریت او ارزښتونه.",
    foundationTitle: "سبا بنسټ",
    foundationDescription: "د سبا بنسټ نوښتونه، ټولنیز خدمتونه او اداري پرمختیايي فعالیتونه.",
    pdcTitle: "د مسلکي پرمختګ مرکز | سبا پوهنتون",
    pdcDescription: "د سبا پوهنتون د مسلکي پرمختګ مرکز، روزنې، ورکشاپونه او د استادانو، محصلانو او اداري کارکوونکو سرچینې.",
    notFoundTitle: "پاڼه ونه موندل شوه | سبا پوهنتون",
    notFoundDescription: "غوښتل شوې پاڼه ونه موندل شوه. د سبا پوهنتون عامه معلوماتو او خدماتو ته دوام ورکړئ.",
  },
  fa: {
    homeTitle: "پوهنتون سبا | برنامه‌های علمی، ثبت نام و تحقیق",
    homeDescription: "وب‌سایت رسمی پوهنتون سبا برای برنامه‌های علمی، ثبت نام آنلاین، نتایج کانکور، تحقیق، خبرها، کتابخانه و خدمات محصلان.",
    contactTitle: "تماس با پوهنتون سبا",
    contactDescription: "معلومات رسمی تماس، آدرس، تلیفون، ایمیل و موقعیت پوهنتون سبا.",
    newsTitle: "خبرها و اعلانات پوهنتون سبا",
    newsDescription: "خبرهای رسمی، اعلانات، فعالیت‌ها و تازه‌ترین معلومات علمی پوهنتون سبا.",
    jobsTitle: "فرصت‌های کاری پوهنتون سبا",
    jobsDescription: "فرصت‌های کاری و اعلانات رسمی استخدام در پوهنتون سبا را ببینید.",
    libraryTitle: "کتابخانه پوهنتون سبا",
    libraryDescription: "خدمات کتابخانه، منابع علمی، کتابخانه آنلاین و حمایت مطالعه در پوهنتون سبا.",
    registrationTitle: "ثبت نام آنلاین | پوهنتون سبا",
    registrationDescription: "ثبت نام آنلاین رسمی پوهنتون سبا برای فارغان صنف دوازدهم و چهاردهم را آغاز کنید.",
    kankorTitle: "نتایج کانکور | پوهنتون سبا",
    kankorDescription: "نتیجه عمومی و تایید شده کانکور پوهنتون سبا را با شناسه رسمی جستجو کنید.",
    researchTitle: "تحقیق در پوهنتون سبا",
    researchDescription: "نشرات تحقیقی، حمایت تحقیق، رهنماها، آموزش‌ها و مجلات علمی پوهنتون سبا را ببینید.",
    studentsTitle: "خدمات محصلان | پوهنتون سبا",
    studentsDescription: "خدمات محصلان، شرایط، پالیسی‌ها، بورسیه‌ها، مشوره و رهنمود علمی پوهنتون سبا.",
    academicsTitle: "برنامه‌های علمی | پوهنتون سبا",
    academicsDescription: "پوهنځی‌ها، دیپارتمنت‌ها، نصاب، لابراتوارها، فیس‌ها، تقویم علمی و معلومات کیفیت پوهنتون سبا.",
    aboutTitle: "درباره پوهنتون سبا",
    aboutDescription: "تاریخ، دستاوردها، رهبری، ساختار، ماموریت و ارزش‌های پوهنتون سبا.",
    foundationTitle: "بنیاد سبا",
    foundationDescription: "ابتکارات بنیاد سبا، خدمات اجتماعی و فعالیت‌های توسعه نهادی.",
    pdcTitle: "مرکز انکشاف مسلکی | پوهنتون سبا",
    pdcDescription: "مرکز انکشاف مسلکی پوهنتون سبا، آموزش‌ها، ورکشاپ‌ها و منابع استادان، محصلان و کارمندان اداری.",
    notFoundTitle: "صفحه پیدا نشد | پوهنتون سبا",
    notFoundDescription: "صفحه مورد نظر پیدا نشد. به معلومات و خدمات عمومی پوهنتون سبا ادامه دهید.",
  },
};

const exactRouteMeta = {
  "/": ["homeTitle", "homeDescription"],
  "/contact": ["contactTitle", "contactDescription"],
  "/news": ["newsTitle", "newsDescription"],
  "/job_opportunity": ["jobsTitle", "jobsDescription"],
  "/library": ["libraryTitle", "libraryDescription"],
  "/library/online_library": ["libraryTitle", "libraryDescription"],
  "/online-registration": ["registrationTitle", "registrationDescription"],
  "/kankor/online-registration": ["registrationTitle", "registrationDescription"],
  "/kankor/results": ["kankorTitle", "kankorDescription"],
  "/kankor/result": ["kankorTitle", "kankorDescription"],
  "/about/academic_programs": ["academicsTitle", "academicsDescription"],
  "/about/organizational_structure": ["aboutTitle", "aboutDescription"],
  "/research/research_publications": ["researchTitle", "researchDescription"],
  "/research/saba_magazine": ["researchTitle", "researchDescription"],
  "/research/published_researche": ["researchTitle", "researchDescription"],
  "/research/activities_resources/published_researche": ["researchTitle", "researchDescription"],
  "/research/research_support": ["researchTitle", "researchDescription"],
  "/research/research_guide": ["researchTitle", "researchDescription"],
  "/research/research_trainings": ["researchTitle", "researchDescription"],
  "/research/research_papers_and_publication": ["researchTitle", "researchDescription"],
  "/students/eligibility_criteria": ["studentsTitle", "studentsDescription"],
  "/students/migration_policy": ["studentsTitle", "studentsDescription"],
  "/students/scholarships_financing": ["studentsTitle", "studentsDescription"],
  "/students/semester_promotion_rules": ["studentsTitle", "studentsDescription"],
  "/students/counseling_center": ["studentsTitle", "studentsDescription"],
  "/foundation": ["foundationTitle", "foundationDescription"],
  "/pdc/a_pdc_b": ["pdcTitle", "pdcDescription"],
};

const routeLabelMeta = {
  "/about/academic_programs": { labelKey: "academics_programs", descriptionKey: "academicsDescription" },
  "/about/organizational_structure": { labelKey: "organizational_structure", descriptionKey: "aboutDescription" },
  "/library/online_library": { labelKey: "online_library", descriptionKey: "libraryDescription" },
  "/library/offline_library": { labelKey: "offline_library", descriptionKey: "libraryDescription" },
  "/library/study_statistic": { labelKey: "study_statistic", descriptionKey: "libraryDescription" },
  "/library/procedure": { labelKey: "procedure", descriptionKey: "libraryDescription" },
  "/library/progressive_plan": { labelKey: "progressive_plan", descriptionKey: "libraryDescription" },
  "/kankor/kankor_guide": { labelKey: "kankor_guide", descriptionKey: "kankorDescription" },
  "/kankor/kankor_registration_date": { labelKey: "kankor_registration_date", descriptionKey: "kankorDescription" },
  "/faculty/cs": { labelKey: "a_bcs_computer_science", descriptionKey: "academicsDescription" },
  "/faculty/eco": { labelKey: "a_bba_economics", descriptionKey: "academicsDescription" },
  "/research/research_publications": { labelKey: "research_publications", descriptionKey: "researchDescription" },
  "/research/saba_magazine": { labelKey: "saba_magazine", descriptionKey: "researchDescription" },
  "/research/published_researche": { labelKey: "published_researche", descriptionKey: "researchDescription" },
  "/research/activities_resources/published_researche": { labelKey: "published_researche", descriptionKey: "researchDescription" },
  "/research/research_support": { labelKey: "research_support", descriptionKey: "researchDescription" },
  "/research/research_guide": { labelKey: "research_guide", descriptionKey: "researchDescription" },
  "/research/research_trainings": { labelKey: "research_trainings", descriptionKey: "researchDescription" },
  "/research/research_papers_and_publication": { labelKey: "research_papers_and_publication", descriptionKey: "researchDescription" },
  "/research/scientific_and_research_magazine": { labelKey: "scientific_and_research_magazine", descriptionKey: "researchDescription" },
  "/research/capacity_building": { labelKey: "capacity_building", descriptionKey: "researchDescription" },
  "/students/eligibility_criteria": { labelKey: "eligibility_criteria", descriptionKey: "studentsDescription" },
  "/students/migration_policy": { labelKey: "migration_policy", descriptionKey: "studentsDescription" },
  "/students/scholarships_financing": { labelKey: "scholarships_financing", descriptionKey: "studentsDescription" },
  "/students/semester_promotion_rules": { labelKey: "semester_promotion_rules", descriptionKey: "studentsDescription" },
  "/students/penalties": { labelKey: "penalties", descriptionKey: "studentsDescription" },
  "/students/counseling_center": { labelKey: "counseling_center", descriptionKey: "studentsDescription" },
  "/students/graduated_students_statistic": { labelKey: "graduated_students_statistic", descriptionKey: "studentsDescription" },
  "/pdc/a_pdc_b": { labelKey: "a_pdc_b", descriptionKey: "pdcDescription" },
  "/pdc/annual_professional_development_plan": { labelKey: "annual_professional_development_plan", descriptionKey: "pdcDescription" },
  "/pdc/seminars_courses_workshop": { labelKey: "seminars_courses_workshop", descriptionKey: "pdcDescription" },
  "/academic/pdc/training_about_pdc/teacher": { labelKey: "teacher", descriptionKey: "pdcDescription" },
  "/academic/pdc/training_about_pdc/administrative_staff": { labelKey: "administrative_staff", descriptionKey: "pdcDescription" },
  "/academic/pdc/training_about_pdc/pdc_student": { labelKey: "pdc_student", descriptionKey: "pdcDescription" },
  "/academic/pdc/training_about_pdc/society": { labelKey: "society", descriptionKey: "pdcDescription" },
};

const labelKeys = {
  about: "about",
  academic: "academics",
  academics: "academics",
  contact: "contact",
  faculty: "faculty",
  foundation: "saba_foundation",
  institutions: "institutions",
  job_opportunity: "job_opportunity",
  kankor: "kankor",
  library: "saba_library",
  news: "news",
  online_registration: "online_registration",
  "online-registration": "online_registration",
  pdc: "a_pdc",
  research: "research",
  students: "student_services",
};

const postTypeMap = {
  news: { storeKey: "news", labelKey: "news", schemaType: "NewsArticle" },
  aggrements: { storeKey: "aggrements", labelKey: "aggrements", schemaType: "Article" },
  teachers: { storeKey: "teachers", labelKey: "teacher", schemaType: "Article" },
  foundation: { storeKey: "foundation", labelKey: "saba_foundation", schemaType: "Article" },
  labs: { storeKey: "labs", labelKey: "labs", schemaType: "Article" },
  pdcstudents: { storeKey: "pdcstudents", labelKey: "pdc_student", schemaType: "Article" },
  administrativestaffs: { storeKey: "administrativestaffs", labelKey: "administrative_staff", schemaType: "Article" },
  publishedresearches: { storeKey: "publishedresearches", labelKey: "publishedresearches", schemaType: "ScholarlyArticle" },
  societies: { storeKey: "societies", labelKey: "society", schemaType: "Article" },
};

const researchPostTypeMap = {
  research_publications: { storeKey: "researchpublications", labelKey: "research_publications", schemaType: "ScholarlyArticle" },
  saba_magazine: { storeKey: "magazines", labelKey: "saba_magazine", schemaType: "ScholarlyArticle" },
};

const multiImageTypeMap = {
  activity: { storeKey: "activities", labelKey: "activity", schemaType: "Article" },
  activities: { storeKey: "activities", labelKey: "activity", schemaType: "Article" },
  achievement: { storeKey: "achievements", labelKey: "achievements", schemaType: "Article" },
  achievements: { storeKey: "achievements", labelKey: "achievements", schemaType: "Article" },
  capacity_building: { storeKey: "capacitybuildings", labelKey: "capacity_building", schemaType: "Article" },
  a_capacity_building: { storeKey: "acapacitybuildings", labelKey: "a_capacity_building", schemaType: "Article" },
  research_trainings: { storeKey: "researchtrainings", labelKey: "research_trainings", schemaType: "Article" },
  research_training: { storeKey: "researchtrainings", labelKey: "research_trainings", schemaType: "Article" },
  research_papers_and_publication: { storeKey: "researchpapersandpublications", labelKey: "research_papers_and_publication", schemaType: "ScholarlyArticle" },
  councils_and_committees: { storeKey: "councilsandcommittees", labelKey: "councils_and_committees", schemaType: "Article" },
  councils_and_committee: { storeKey: "councilsandcommittees", labelKey: "councils_and_committees", schemaType: "Article" },
};

const stripHtml = (value = "") =>
  String(value)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (value, limit = 155) => {
  const clean = stripHtml(value);
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).trim()}...`;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const getId = (record) => record?._id || record?.publicId || record?.id || "";

const findRecord = (records, id) =>
  asArray(records).find((record) => String(getId(record)) === String(id));

const localizedField = (record, field, languageCode) =>
  stripHtml(languages.getLocalizedField(record, field, languageCode));

const titleField = (record, languageCode) =>
  localizedField(record, "title", languageCode) ||
  localizedField(record, "name", languageCode) ||
  localizedField(record, "jobTitle", languageCode);

const descriptionField = (record, languageCode) =>
  localizedField(record, "description", languageCode) ||
  localizedField(record, "message", languageCode) ||
  localizedField(record, "valueText", languageCode) ||
  titleField(record, languageCode);

const imageField = (record) =>
  record?.thumbnail ||
  record?.thumbnailPath ||
  record?.image ||
  record?.imagePath ||
  record?.photo ||
  record?.photoPath ||
  asArray(record?.images).find((image) => image?.filePath || image?.url)?.filePath ||
  DEFAULT_SOCIAL_IMAGE;

const copyFor = (key, languageCode) =>
  pageCopy[languageCode]?.[key] || pageCopy.en[key] || "";

const withSiteName = (title) => {
  const cleanTitle = stripHtml(title);
  if (!cleanTitle) return SITE_NAME;
  return cleanTitle.toLowerCase().includes(SITE_NAME.toLowerCase()) ? cleanTitle : `${cleanTitle} | ${SITE_NAME}`;
};

const routeLabel = (segment) => {
  const normalized = String(segment || "").replace(/-/g, "_");
  const key = labelKeys[segment] || labelKeys[normalized] || normalized;
  return languages[key] || normalized.replace(/_/g, " ");
};

const descriptionWithLabel = (label, description) => {
  const cleanLabel = stripHtml(label);
  const cleanDescription = stripHtml(description);
  if (!cleanLabel) return cleanDescription || DEFAULT_DESCRIPTION;
  return cleanDescription ? `${cleanLabel}: ${cleanDescription}` : DEFAULT_DESCRIPTION;
};

const buildBreadcrumb = (pathname, languageCode) => {
  const segments = normalizePathname(pathname).split("/").filter(Boolean);
  const itemListElement = [
    {
      "@type": "ListItem",
      position: 1,
      name: languages.home || "Home",
      item: absoluteSiteUrl("/"),
    },
  ];

  const institutional = institutionalRoutes[normalizePathname(pathname)];
  if (institutional) {
    itemListElement.push({ "@type": "ListItem", position: 2, name: languages[institutional.titleKey], item: localizedRouteUrl(pathname, languageCode) });
    return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement };
  }

  let currentPath = "";
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    itemListElement.push({
      "@type": "ListItem",
      position: index + 2,
      name: routeLabel(segment),
      item: localizedRouteUrl(currentPath, languageCode),
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement,
  };
};

const organizationSchema = (globalState, languageCode) => {
  const contact = asArray(globalState?.contactinfos)[0] || {};
  const address = localizedField(contact, "address", languageCode) || contact.address;
  const sameAs = [contact.facebook, contact.youtube].filter(Boolean);

  return {
    "@context": "https://schema.org",
    "@type": ["CollegeOrUniversity", "Organization"],
    "@id": `${PUBLIC_SITE_URL}/#university`,
    name: SITE_NAME,
    alternateName: ["Saba University", "سبا پوهنتون", "پوهنتون سبا"],
    url: PUBLIC_SITE_URL,
    logo: absoluteAssetUrl("/logo512.png"),
    image: absoluteAssetUrl(DEFAULT_SOCIAL_IMAGE),
    email: contact.email || undefined,
    telephone: contact.phone || undefined,
    address: address
      ? {
          "@type": "PostalAddress",
          streetAddress: address,
          addressCountry: "AF",
        }
      : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  };
};

const buildArticleSchema = ({ record, schemaType, pathname, languageCode }) => {
  const title = titleField(record, languageCode);
  const description = truncate(descriptionField(record, languageCode), 240);
  const image = imageField(record);

  return {
    "@context": "https://schema.org",
    "@type": schemaType || "Article",
    headline: title || SITE_NAME,
    description,
    image: absoluteAssetUrl(image),
    mainEntityOfPage: localizedRouteUrl(pathname, languageCode),
    datePublished: record?.createdAt || record?.startDate || undefined,
    dateModified: record?.updatedAt || record?.createdAt || undefined,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: absoluteAssetUrl("/logo512.png"),
      },
    },
    author: record?.author
      ? {
          "@type": "Person",
          name: record.author,
        }
      : {
          "@type": "Organization",
          name: SITE_NAME,
        },
  };
};

const buildItemListSchema = ({ records, pathname, languageCode, itemType = "Article" }) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: asArray(records)
    .slice(0, 12)
    .map((record, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: localizedRouteUrl(`${pathname}/${getId(record)}`, languageCode),
      item: {
        "@type": itemType,
        name: titleField(record, languageCode),
        description: truncate(descriptionField(record, languageCode), 180),
        image: absoluteAssetUrl(imageField(record)),
      },
    })),
});

const jobPostingSchemas = (jobs, languageCode) =>
  asArray(jobs)
    .slice(0, 12)
    .map((job) => ({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: titleField(job, languageCode) || languages.job_opportunity || "Job Opportunity",
      description: truncate(descriptionField(job, languageCode), 500),
      datePosted: job.createdAt || job.startDate || undefined,
      validThrough: job.expireDate || job.endDate || undefined,
      employmentType: job.type || undefined,
      hiringOrganization: {
        "@type": "Organization",
        name: SITE_NAME,
        sameAs: PUBLIC_SITE_URL,
        logo: absoluteAssetUrl("/logo512.png"),
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressCountry: "AF",
        },
      },
    }));

const facultySchema = (pathname, title, description, languageCode) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name: title,
  description,
  url: localizedRouteUrl(pathname, languageCode),
  provider: {
    "@type": "CollegeOrUniversity",
    name: SITE_NAME,
    sameAs: PUBLIC_SITE_URL,
  },
});

const staticMeta = (pathname, languageCode) => {
  if (pathname === "/academics/masters" || pathname.startsWith("/academics/masters/")) {
    return { title: withSiteName(languages.master_programs), description: languages.master_intro };
  }
  const institutional = institutionalRoutes[pathname];
  if (institutional) {
    const title = withSiteName(languages[institutional.titleKey]);
    const description = languages[institutional.descriptionKey];
    return { title, description, schemas: [{ "@context": "https://schema.org", "@type": institutional.schemaType,
      "@id": `${localizedRouteUrl(pathname, languageCode)}#webpage`, name: title, description,
      url: localizedRouteUrl(pathname, languageCode), inLanguage: languageCode, about: { "@id": `${PUBLIC_SITE_URL}/#university` } }] };
  }
  const labelMeta = routeLabelMeta[pathname];
  if (labelMeta) {
    const segments = pathname.split("/").filter(Boolean);
    const label = languages[labelMeta.labelKey] || routeLabel(segments[segments.length - 1]);
    const description = copyFor(labelMeta.descriptionKey, languageCode) || DEFAULT_DESCRIPTION;
    return {
      title: withSiteName(label),
      description: descriptionWithLabel(label, description),
    };
  }

  const routeKeys = exactRouteMeta[pathname];
  if (!routeKeys) return null;
  return {
    title: withSiteName(copyFor(routeKeys[0], languageCode)),
    description: copyFor(routeKeys[1], languageCode) || DEFAULT_DESCRIPTION,
  };
};

const derivedStaticMeta = (pathname, languageCode) => {
  if (pathname === "/404" || pathname === "/about/history_and_achievements") {
    return {
      title: copyFor("notFoundTitle", languageCode),
      description: copyFor("notFoundDescription", languageCode),
      robots: "noindex,follow",
    };
  }

  if (pathname.startsWith("/students/")) {
    return {
      title: copyFor("studentsTitle", languageCode),
      description: copyFor("studentsDescription", languageCode),
    };
  }

  if (pathname.startsWith("/research/")) {
    return {
      title: copyFor("researchTitle", languageCode),
      description: copyFor("researchDescription", languageCode),
    };
  }

  if (pathname.startsWith("/academic/") || pathname.startsWith("/faculty/")) {
    return {
      title: copyFor("academicsTitle", languageCode),
      description: copyFor("academicsDescription", languageCode),
    };
  }

  if (pathname.startsWith("/about/")) {
    return {
      title: copyFor("aboutTitle", languageCode),
      description: copyFor("aboutDescription", languageCode),
    };
  }

  const segments = pathname.split("/").filter(Boolean);
  const label = segments.length ? routeLabel(segments[segments.length - 1]) : SITE_NAME;
  return {
    title: withSiteName(label),
    description: DEFAULT_DESCRIPTION,
  };
};

const dynamicPostMeta = ({ pathname, globalState, languageCode }) => {
  const postMatch = matchPath("/posts/:type/:id", pathname);
  if (postMatch) {
    const config = postTypeMap[postMatch.params.type];
    const record = config && findRecord(globalState?.[config.storeKey], postMatch.params.id);
    if (!record) return null;
    const title = titleField(record, languageCode);
    return {
      title: withSiteName(title),
      description: truncate(descriptionField(record, languageCode), 155),
      image: absoluteAssetUrl(imageField(record)),
      ogType: config.schemaType === "NewsArticle" ? "article" : "article",
      schemas: [buildArticleSchema({ record, schemaType: config.schemaType, pathname, languageCode })],
    };
  }

  const researchMatch = matchPath("/research/post/:type/:id", pathname);
  if (researchMatch) {
    const config = researchPostTypeMap[researchMatch.params.type];
    const record = config && findRecord(globalState?.[config.storeKey], researchMatch.params.id);
    if (!record) return null;
    const title = titleField(record, languageCode);
    return {
      title: withSiteName(title),
      description: truncate(descriptionField(record, languageCode), 155),
      image: absoluteAssetUrl(imageField(record)),
      ogType: "article",
      schemas: [buildArticleSchema({ record, schemaType: config.schemaType, pathname, languageCode })],
    };
  }

  const multiMatch = matchPath("/multipleimgs/:type/:id", pathname);
  if (multiMatch) {
    const config = multiImageTypeMap[multiMatch.params.type];
    const record = config && findRecord(globalState?.[config.storeKey], multiMatch.params.id);
    if (!record) return null;
    const title = titleField(record, languageCode);
    return {
      title: withSiteName(title),
      description: truncate(descriptionField(record, languageCode), 155),
      image: absoluteAssetUrl(imageField(record)),
      ogType: "article",
      schemas: [buildArticleSchema({ record, schemaType: config.schemaType, pathname, languageCode })],
    };
  }

  return null;
};

const listSchemasForRoute = ({ pathname, globalState, languageCode }) => {
  if (pathname === "/news") {
    return [buildItemListSchema({ records: globalState?.news, pathname: "/posts/news", languageCode, itemType: "NewsArticle" })];
  }
  if (pathname === "/job_opportunity") return jobPostingSchemas(globalState?.jobs, languageCode);
  if (pathname === "/research/published_researche" || pathname === "/research/activities_resources/published_researche") {
    return [buildItemListSchema({ records: globalState?.publishedresearches, pathname: "/posts/publishedresearches", languageCode, itemType: "ScholarlyArticle" })];
  }
  if (pathname === "/research/research_publications") {
    return [buildItemListSchema({ records: globalState?.researchpublications, pathname: "/research/post/research_publications", languageCode, itemType: "ScholarlyArticle" })];
  }
  if (pathname === "/research/saba_magazine") {
    return [buildItemListSchema({ records: globalState?.magazines, pathname: "/research/post/saba_magazine", languageCode, itemType: "ScholarlyArticle" })];
  }
  return [];
};

const buildSeo = ({ pathname, search, globalState, languageCode }) => {
  const cleanPath = normalizePathname(pathname);
  const dynamic = dynamicPostMeta({ pathname: cleanPath, globalState, languageCode });
  const base = dynamic || staticMeta(cleanPath, languageCode) || derivedStaticMeta(cleanPath, languageCode);
  const isSearchPage = cleanPath === "/search";
  const searchableParams = new URLSearchParams(search || "");
  const hasNonLanguageQuery = Array.from(searchableParams.keys()).some((key) => key !== "lang");
  const isSharedKankorResult = Boolean(matchPath("/kankor-results/:resultId", cleanPath) || matchPath("/kankor/result/:resultId", cleanPath));
  const isFacultyPage = Boolean(matchPath("/faculty/:id", cleanPath));
  const safeKankorTitle = copyFor("kankorTitle", languageCode);
  const safeKankorDescription = copyFor("kankorDescription", languageCode);

  const title = isSharedKankorResult ? safeKankorTitle : base.title;
  const description = isSharedKankorResult ? safeKankorDescription : base.description;
  const robots =
    base.robots ||
    (isSearchPage || isSharedKankorResult || hasNonLanguageQuery ? "noindex,follow" : "index,follow,max-image-preview:large");

  const schemas = [
    organizationSchema(globalState, languageCode),
    buildBreadcrumb(cleanPath, languageCode),
    ...(base.schemas || []),
    ...listSchemasForRoute({ pathname: cleanPath, globalState, languageCode }),
  ];

  if (isFacultyPage && !dynamic) {
    schemas.push(facultySchema(cleanPath, title, description, languageCode));
  }

  return {
    title,
    description,
    canonical: localizedRouteUrl(cleanPath, languageCode),
    image: absoluteAssetUrl(base.image || DEFAULT_SOCIAL_IMAGE),
    ogType: base.ogType || "website",
    robots,
    schemas,
    pathname: cleanPath,
  };
};

const hasServerBackedDynamicSeoRoute = (pathname) =>
  Boolean(
    institutionalRoutes[pathname] ||
    pathname === "/academics/masters" ||
    matchPath("/academics/masters/:slug", pathname) ||
    matchPath("/posts/:type/:id", pathname) ||
    matchPath("/research/post/:type/:id", pathname) ||
    matchPath("/multipleimgs/:type/:id", pathname)
  );

const mergeServerSeo = (localSeo, serverMetadata) => {
  if (!serverMetadata || typeof serverMetadata !== "object") return localSeo;
  const openGraph = serverMetadata.openGraph || {};
  const twitter = serverMetadata.twitter || {};
  return {
    ...localSeo,
    title: serverMetadata.title || localSeo.title,
    description: serverMetadata.description || localSeo.description,
    canonical: serverMetadata.canonical || localSeo.canonical,
    image: openGraph.image || twitter.image || localSeo.image,
    ogType: openGraph.type || localSeo.ogType,
    robots: localSeo.robots?.startsWith("noindex") ? localSeo.robots : serverMetadata.robots || localSeo.robots,
    schemas: Array.isArray(serverMetadata.structuredData) ? serverMetadata.structuredData : localSeo.schemas,
  };
};

const ensureMeta = (selector, identity, values) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(identity).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) element.setAttribute(key, value);
  });
};

const ensureLink = (selector, identity, values) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    Object.entries(identity).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  Object.entries(values).forEach(([key, value]) => element.setAttribute(key, value));
};

const upsertJsonLd = (id, data) => {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.id = id;
    element.setAttribute("data-managed-seo", "jsonld");
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
};

const removeManagedJsonLd = () => {
  document.head.querySelectorAll('script[data-managed-seo="jsonld"]').forEach((element) => element.remove());
};

export default function RouteSeo({ languageCode, direction }) {
  const location = useLocation();
  const context = useContext(AuthContext);
  const [globalState] = useStore();
  const activeLanguage = languages.normalizeLanguage(languageCode || context.languageCode || languages.getLanguage());

  const [serverSeo, setServerSeo] = useState(null);

  const seo = useMemo(
    () =>
      buildSeo({
        pathname: location.pathname,
        search: location.search,
        globalState,
        languageCode: activeLanguage,
      }),
    [location.pathname, location.search, globalState, activeLanguage]
  );

  const effectiveSeo = useMemo(() => {
    const key = `${normalizePathname(location.pathname)}|${activeLanguage}`;
    return serverSeo?.key === key ? serverSeo.seo : seo;
  }, [activeLanguage, location.pathname, seo, serverSeo]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const pathname = normalizePathname(location.pathname);
    const key = `${pathname}|${activeLanguage}`;
    if (!hasServerBackedDynamicSeoRoute(pathname)) {
      setServerSeo(null);
      return undefined;
    }

    const controller = new AbortController();
    const url = new URL("/api/seo/meta", `${API_BASE_URL}/`);
    url.searchParams.set("path", pathname);
    url.searchParams.set("lang", activeLanguage);

    fetch(url.href, { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (payload?.status === "success" && payload.data) {
          setServerSeo({ key, seo: mergeServerSeo(seo, payload.data) });
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError") setServerSeo(null);
      });

    return () => controller.abort();
  }, [activeLanguage, location.pathname, seo]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const activeSeo = effectiveSeo;
    const dir = direction || languages.getLanguageDirection(activeLanguage);
    document.documentElement.lang = activeLanguage;
    document.documentElement.dir = dir;
    document.documentElement.dataset.language = activeLanguage;
    document.title = activeSeo.title;

    ensureMeta('meta[name="description"]', { name: "description" }, { content: activeSeo.description });
    ensureMeta('meta[name="robots"]', { name: "robots" }, { content: activeSeo.robots });
    ensureMeta('meta[name="theme-color"]', { name: "theme-color" }, { content: "#0b6ea8" });
    ensureLink('link[rel="canonical"]', { rel: "canonical" }, { href: activeSeo.canonical });

    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
    SUPPORTED_SEO_LANGUAGES.forEach((lang) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = lang;
      link.href = localizedRouteUrl(activeSeo.pathname, lang);
      link.setAttribute("data-managed-seo", "alternate");
      document.head.appendChild(link);
    });
    const defaultLink = document.createElement("link");
    defaultLink.rel = "alternate";
    defaultLink.hreflang = "x-default";
    defaultLink.href = absoluteSiteUrl(activeSeo.pathname);
    defaultLink.setAttribute("data-managed-seo", "alternate");
    document.head.appendChild(defaultLink);

    ensureMeta('meta[property="og:title"]', { property: "og:title" }, { content: activeSeo.title });
    ensureMeta('meta[property="og:description"]', { property: "og:description" }, { content: activeSeo.description });
    ensureMeta('meta[property="og:type"]', { property: "og:type" }, { content: activeSeo.ogType });
    ensureMeta('meta[property="og:url"]', { property: "og:url" }, { content: activeSeo.canonical });
    ensureMeta('meta[property="og:site_name"]', { property: "og:site_name" }, { content: SITE_NAME });
    ensureMeta('meta[property="og:locale"]', { property: "og:locale" }, { content: activeLanguage === "en" ? "en_US" : activeLanguage === "ps" ? "ps_AF" : "fa_AF" });
    ensureMeta('meta[property="og:image"]', { property: "og:image" }, { content: activeSeo.image });
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }, { content: "summary_large_image" });
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }, { content: activeSeo.title });
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }, { content: activeSeo.description });
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }, { content: activeSeo.image });

    removeManagedJsonLd();
    document.head.querySelectorAll('script[data-prerendered-seo="true"]').forEach((element) => element.remove());
    activeSeo.schemas.filter(Boolean).forEach((schema, index) => upsertJsonLd(`saba-seo-jsonld-${index}`, schema));
  }, [activeLanguage, direction, effectiveSeo]);

  return null;
}
